import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';

export type RelatedPost = {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  featuredImageUrl: string | null;
  featuredImageAlt: string;
  category: string;
};

/**
 * Horizontal slider of suggested articles shown at the end of a blog post to
 * keep the reader on the blog. Scrolls by drag/swipe and via the arrow buttons.
 */
export function RelatedPosts({ posts }: { posts: RelatedPost[] }) {
  const scrollRef = useRef<HTMLDivElement>(null);

  if (posts.length === 0) return null;

  function scroll(dir: -1 | 1) {
    scrollRef.current?.scrollBy({ left: dir * 280, behavior: 'smooth' });
  }

  return (
    <section className="mt-12 pt-8 border-t border-slate-200">
      <div className="flex items-center justify-between gap-4 mb-4">
        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <Sparkles size={18} className="text-brand-600" />
          Artículos que te pueden gustar
        </h2>
        <div className="hidden sm:flex items-center gap-1.5">
          <button
            onClick={() => scroll(-1)}
            aria-label="Anterior"
            className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-brand-600 hover:border-brand-300 transition"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={() => scroll(1)}
            aria-label="Siguiente"
            className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-brand-600 hover:border-brand-300 transition"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory py-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        {posts.map((p) => (
          <Link
            key={p.id}
            to={`/blog/${p.slug}`}
            className="group shrink-0 w-[250px] snap-start bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col hover:border-brand-300 hover:shadow-md transition"
          >
            <div className="aspect-[16/9] bg-slate-100 overflow-hidden">
              {p.featuredImageUrl && (
                <img
                  src={p.featuredImageUrl}
                  alt={p.featuredImageAlt}
                  loading="lazy"
                  className="h-full w-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
                />
              )}
            </div>
            <div className="p-4 flex flex-col flex-1">
              {p.category && (
                <span className="text-[11px] font-semibold text-brand-600 uppercase tracking-wide">
                  {p.category}
                </span>
              )}
              <h3 className="mt-1 font-bold text-sm text-slate-900 leading-snug line-clamp-3 group-hover:text-brand-700 transition">
                {p.title}
              </h3>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
